<template>
    <div>
        <div v-if="!hideTabs && !isMobile" class="hidden sm:block">
            <Tabs v-model="activeTab" class="w-full">
                <div class="flex items-center">
                    <TabsList
                        :class="['flex items-center', stretch ? 'w-full grid' : '']"
                        :style="stretch ? { gridTemplateColumns: `repeat(${tabs.filter((tab) => !getTab(tab)?.newTabUrl).length}, 1fr)` } : {}">
                        <template v-for="tab in tabs" :key="getTabKey(tab)">
                            <!-- External link tabs - act as triggers, not selectable tabs -->
                            <a
                                v-if="getTab(tab)?.newTabUrl"
                                :href="getTab(tab)?.href"
                                :target="getTab(tab)?.target || '_blank'"
                                :rel="getTab(tab)?.rel || 'noopener noreferrer'"
                                :class="[
                                    'inline-flex items-center justify-center whitespace-nowrap rounded-md px-2 py-1 text-sm font-medium transition-all cursor-pointer',
                                    'text-foreground dark:text-muted-foreground',
                                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                                    'hover:bg-muted hover:text-muted-foreground',
                                    getTab(tab)?.disabled ? 'pointer-events-none opacity-50 cursor-not-allowed' : ''
                                ]">
                                <component
                                    :is="tab?.icon?.icon"
                                    v-if="tab?.icon?.left"
                                    :class="`${tab?.icon?.size || 'h-4 w-4'} mr-2 ${tab?.icon?.weight || ''}`" />
                                {{ getTabName(tab) }}
                                <ExternalLinkIcon class="h-4 w-4 ml-2" />
                                <component
                                    :is="tab?.icon?.icon"
                                    v-if="tab?.icon?.right"
                                    :class="`${tab?.icon?.size || 'h-4 w-4'} ml-2 ${tab?.icon?.weight || ''}`" />
                            </a>
                            <TabsTrigger v-else :value="getTabKey(tab)" :disabled="getTab(tab)?.disabled">
                                <component
                                    :is="tab?.icon?.icon"
                                    v-if="tab?.icon?.left"
                                    :class="`${tab?.icon?.size || 'h-4 w-4'} mr-2 ${tab?.icon?.weight || ''}`"
                                    @click="tab?.icon?.routePathName ? toPath(tab?.icon?.routePathName) : null" />
                                {{ getTabName(tab) }}
                                <component
                                    :is="tab?.icon?.icon"
                                    v-if="tab?.icon?.right"
                                    :class="`${tab?.icon?.size || 'h-4 w-4'} ml-2 ${tab?.icon?.weight || ''}`"
                                    @click="tab?.icon?.routePathName ? toPath(tab?.icon?.routePathName) : null" />
                            </TabsTrigger>
                        </template>
                    </TabsList>
                    <!-- New Slot for Additional Components -->
                    <div class="ml-4">
                        <slot name="tabs-end" />
                    </div>
                </div>
                <AppLoader :force-loading="loading" default-height="20vh">
                    <TabsContent
                        v-for="tab in tabs.filter((tab) => !getTab(tab)?.newTabUrl)"
                        :key="getTabKey(tab)"
                        :value="getTabKey(tab)"
                        class="tab-content overflow-y-visible">
                        <slot :name="getTabKey(tab)" />
                    </TabsContent>
                </AppLoader>
            </Tabs>
        </div>
        
        <!-- Mobile version -->
        <div v-if="!hideTabs && isMobile" class="sm:hidden">
            <Select v-model="activeTab" class="w-full mb-4">
                <SelectTrigger class="w-full">
                    <SelectValue :placeholder="getTabName(activeTab)" />
                </SelectTrigger>
                <SelectContent>
                    <SelectItem
                        v-for="tab in tabs.filter((tab) => !getTab(tab)?.newTabUrl)"
                        :key="getTabKey(tab)"
                        :value="getTabKey(tab)"
                        :disabled="getTab(tab)?.disabled">
                        <div class="flex items-center">
                            <component
                                :is="tab?.icon?.icon"
                                v-if="tab?.icon?.left"
                                :class="`${tab?.icon?.size || 'h-4 w-4'} mr-2 ${tab?.icon?.weight || ''}`" />
                            {{ getTabName(tab) }}
                            <component
                                :is="tab?.icon?.icon"
                                v-if="tab?.icon?.right"
                                :class="`${tab?.icon?.size || 'h-4 w-4'} ml-2 ${tab?.icon?.weight || ''}`" />
                        </div>
                    </SelectItem>
                </SelectContent>
            </Select>
            
            <AppLoader :force-loading="loading" default-height="20vh">
                <div
                    v-for="tab in tabs.filter((tab) => !getTab(tab)?.newTabUrl)"
                    :key="getTabKey(tab)"
                    v-show="activeTab === getTabKey(tab)"
                    class="tab-content overflow-y-visible">
                    <slot :name="getTabKey(tab)" />
                </div>
            </AppLoader>
        </div>
    </div>
</template>

<script setup>
import { ref, watch, computed, onMounted, onBeforeUnmount } from 'vue';
import { useRouter } from 'vue-router';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ExternalLinkIcon } from 'lucide-vue-next';
import AppLoader from '../../components/AppLoader.vue';

const props = defineProps({
    tabs: Array,
    initialActiveTab: [String, Object],
    loading: Boolean,
    hideTabs: Boolean,
    stretch: {
        type: Boolean,
        default: false
    }
});

const emit = defineEmits(['tab-change']);
const router = useRouter();

// Track screen size for responsive rendering
const isMobile = ref(false);

// Check screen size
const checkScreenSize = () => {
    isMobile.value = window.innerWidth < 640; // sm breakpoint
};

// Initialize and add resize listener
onMounted(() => {
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
});

// Cleanup listener
onBeforeUnmount(() => {
    window.removeEventListener('resize', checkScreenSize);
});

// Helper function to get first non-external tab
function getFirstSelectableTab() {
    const selectableTab = props.tabs.find((tab) => !getTab(tab)?.newTabUrl);
    return selectableTab ? getTabKey(selectableTab) : '';
}

const activeTab = ref(props.initialActiveTab || getFirstSelectableTab());

watch(
    () => props.initialActiveTab,
    (newVal) => {
        if (newVal) {
            activeTab.value = typeof newVal === 'object' ? getTabKey(newVal) : newVal;
        }
    },
    { immediate: true }
);

watch(activeTab, (newVal) => emit('tab-change', newVal));

function getTabName(tab) {
    return typeof tab === 'object' && tab.name ? tab.name : tab;
}

function getTabKey(tab) {
    return typeof tab === 'object' ? tab.key || tab.name : tab;
}

function getTab(tabInfo) {
    return typeof tabInfo === 'object' ? tabInfo : props.tabs.find((tab) => getTabKey(tab) === tabInfo);
}

function toPath(pathName) {
    return router.push({ name: pathName });
}
</script>

<style scoped>
/* Add any additional styles if needed */
</style>
